import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import {
  createDashboard,
  createWidget,
  deleteDashboard,
  deleteWidget,
  getDashboard,
  getDashboards,
  getDefaultDashboard,
  getRecordGroupDashboard,
  moveWidget,
  queryDashboardAnalytics,
  resizeWidget,
  reorderWidgets,
  updateDashboard,
  updateWidget,
  updateWidgetVisibility,
} from '@/services/api/dashboard.service'
import type {
  DashboardAnalyticsQueryRequest,
  DashboardCreateRequest,
  DashboardUpdateRequest,
  DashboardWidgetCreateRequest,
  DashboardWidgetUpdateRequest,
  WidgetVisibilityRequest,
  WidgetReorderRequest,
} from '@/types/api/dashboard'

import { useWorkspace } from './useWorkspace'

/**
 * Workspace dashboards (selected id or the default), or — with {@code recordGroupId} — that record
 * group's own dashboard.
 */
export function useDashboard(initialDashboardId?: string, options?: { recordGroupId?: string }) {
  const { currentWorkspaceId } = useWorkspace()
  const queryClient = useQueryClient()
  const recordGroupId = options?.recordGroupId
  const [selectedDashboardId, setSelectedDashboardId] = useState<string | undefined>(initialDashboardId)

  const dashboardQuery = useQuery({
    queryKey: recordGroupId
      ? ['workspace', currentWorkspaceId, 'dashboard', 'record-group', recordGroupId]
      : ['workspace', currentWorkspaceId, 'dashboard', selectedDashboardId ?? 'default'],
    queryFn: () => recordGroupId
      ? getRecordGroupDashboard(recordGroupId)
      : selectedDashboardId ? getDashboard(selectedDashboardId) : getDefaultDashboard(),
    enabled: !!currentWorkspaceId,
    staleTime: 30 * 1000,
    retry: recordGroupId ? false : undefined,
  })

  // Sync selectedDashboardId when the URL param changes
  useEffect(() => {
    if (initialDashboardId) {
      setSelectedDashboardId(initialDashboardId)
    }
  }, [initialDashboardId])

  // Sync selectedDashboardId once loaded if we fell back to default
  useEffect(() => {
    if (!recordGroupId && !selectedDashboardId && dashboardQuery.data?.id) {
      setSelectedDashboardId(dashboardQuery.data.id)
    }
  }, [dashboardQuery.data?.id, selectedDashboardId, recordGroupId])

  const dashboardsQuery = useQuery({
    queryKey: ['workspace', currentWorkspaceId, 'dashboards'],
    queryFn: () => getDashboards(),
    enabled: !!currentWorkspaceId && !recordGroupId,
    staleTime: 30 * 1000,
  })

  const createMutation = useMutation({
    mutationFn: (data: DashboardCreateRequest) => createDashboard(data),
    onSuccess: (dashboard) => {
      queryClient.setQueryData(
        ['workspace', currentWorkspaceId, 'dashboard', dashboard.id],
        dashboard
      )
      if (dashboard.id) setSelectedDashboardId(dashboard.id)
      queryClient.invalidateQueries({ queryKey: ['workspace', currentWorkspaceId, 'dashboards'] })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ dashboardId, data }: { dashboardId: string; data: DashboardUpdateRequest }) =>
      updateDashboard(dashboardId, data),
    onSuccess: (dashboard) => {
      queryClient.setQueryData(
        ['workspace', currentWorkspaceId, 'dashboard', dashboard.id],
        dashboard
      )
      if (recordGroupId) {
        queryClient.setQueryData(['workspace', currentWorkspaceId, 'dashboard', 'record-group', recordGroupId], dashboard)
      }
      queryClient.invalidateQueries({ queryKey: ['workspace', currentWorkspaceId, 'dashboards'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteDashboard,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace', currentWorkspaceId, 'dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['workspace', currentWorkspaceId, 'dashboards'] })
    },
  })

  const analyticsMutation = useMutation({
    mutationFn: (data: DashboardAnalyticsQueryRequest) => queryDashboardAnalytics(data),
  })

  // ---- Widget mutations ----

  const createWidgetMutation = useMutation({
    mutationFn: (data: DashboardWidgetCreateRequest & { dashboardId: string }) =>
      createWidget(data.dashboardId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace', currentWorkspaceId, 'dashboard'] })
    },
  })

  const updateWidgetMutation = useMutation({
    mutationFn: ({ dashboardId, widgetId, data }: {
      dashboardId: string; widgetId: string; data: DashboardWidgetUpdateRequest
    }) => updateWidget(dashboardId, widgetId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace', currentWorkspaceId, 'dashboard'] })
    },
  })

  const deleteWidgetMutation = useMutation({
    mutationFn: ({ dashboardId, widgetId }: { dashboardId: string; widgetId: string }) =>
      deleteWidget(dashboardId, widgetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace', currentWorkspaceId, 'dashboard'] })
    },
  })

  const moveWidgetMutation = useMutation({
    mutationFn: ({ dashboardId, widgetId, x, y }: {
      dashboardId: string; widgetId: string; x: number; y: number
    }) => moveWidget(dashboardId, widgetId, x, y),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace', currentWorkspaceId, 'dashboard'] })
    },
  })

  const resizeWidgetMutation = useMutation({
    mutationFn: ({ dashboardId, widgetId, w, h }: {
      dashboardId: string; widgetId: string; w: number; h: number
    }) => resizeWidget(dashboardId, widgetId, w, h),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace', currentWorkspaceId, 'dashboard'] })
    },
  })

  const widgetVisibilityMutation = useMutation({
    mutationFn: ({ dashboardId, widgetId, data }: {
      dashboardId: string; widgetId: string; data: WidgetVisibilityRequest
    }) => updateWidgetVisibility(dashboardId, widgetId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace', currentWorkspaceId, 'dashboard'] })
    },
  })

  const reorderWidgetsMutation = useMutation({
    mutationFn: ({ dashboardId, data }: {
      dashboardId: string; data: WidgetReorderRequest
    }) => reorderWidgets(dashboardId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace', currentWorkspaceId, 'dashboard'] })
    },
  })

  const loadedDashboardId = dashboardQuery.data?.id
  useEffect(() => {
    if (loadedDashboardId) {
      queryClient.prefetchQuery({
        queryKey: ['dashboard', loadedDashboardId],
        queryFn: () => getDashboard(loadedDashboardId),
      })
    }
  }, [loadedDashboardId, queryClient])

  return {
    dashboard: dashboardQuery.data,
    dashboards: dashboardsQuery.data ?? [],
    selectedDashboardId,
    selectDashboard: setSelectedDashboardId,
    isLoading: dashboardQuery.isLoading,
    error: dashboardQuery.error,
    createDashboard: createMutation.mutate,
    updateDashboard: updateMutation.mutate,
    deleteDashboard: deleteMutation.mutate,
    queryAnalytics: analyticsMutation.mutateAsync,
    createWidget: createWidgetMutation.mutateAsync,
    updateWidget: updateWidgetMutation.mutateAsync,
    deleteWidget: deleteWidgetMutation.mutateAsync,
    moveWidget: moveWidgetMutation.mutateAsync,
    resizeWidget: resizeWidgetMutation.mutateAsync,
    reorderWidgets: reorderWidgetsMutation.mutateAsync,
    updateWidgetVisibility: widgetVisibilityMutation.mutateAsync,
    isSaving: createMutation.isPending || updateMutation.isPending
      || createWidgetMutation.isPending || updateWidgetMutation.isPending
      || moveWidgetMutation.isPending || resizeWidgetMutation.isPending
      || reorderWidgetsMutation.isPending,
    isDeleting: deleteMutation.isPending || deleteWidgetMutation.isPending,
    isQueryingAnalytics: analyticsMutation.isPending,
  }
}
